/**
 * 起動時の組み立て。
 *
 * `initializeApp` を2回呼ばない(React の StrictMode は effect を二度走らせる)ため、
 * モジュールスコープで1回だけ作って使い回す。
 */
import { AdminAuthService } from './adminAuth';
import {
  createCatalogDraftReader,
  createCatalogDraftSaver,
  type CatalogDraftReader,
  type CatalogDraftSaver,
} from './catalog';
import { createCmsCallables, createCmsReader, type CmsCallables, type CmsReader } from './cms';
import { ContentLoader } from './content';
import { resolveAdminContentSource, type AdminContentSource } from './contentSource';
import {
  missingAdminEnvKeys,
  readAdminEnv,
  readAssetsBase,
  BUNDLED_CONTENT_BASE,
  readContentBase,
  readWebAppUrl,
} from './env';
import { createAdminFirebase, type AdminFirebase } from './firebase';
import { QuizLoader } from './quiz';

export interface ReadyBootstrap {
  readonly kind: 'ready';
  readonly firebase: AdminFirebase;
  readonly auth: AdminAuthService;
  readonly content: ContentLoader;
  /** まなび(5教科クイズ)。タイピングとは別ファイル群なのでローダも分ける。 */
  readonly quiz: QuizLoader;
  /** 画像・音声の配信元。null なら画面ではサムネイル・再生ボタンを出さない。 */
  readonly assetsBase: string | null;
  /** 閲覧画面が表示している版(現在の公開版。取れなければ同梱 bundled-v1)。 */
  readonly contentSource: Promise<AdminContentSource>;
  /** 管理用 callable(公開・権限。docs/20)。 */
  readonly cms: CmsCallables;
  /** 公開版・監査ログの読み取り。 */
  readonly cmsReader: CmsReader;
  /** ゲーム(Web)の公開URL。null なら「ゲームで試す」リンクを出さない。 */
  readonly webAppUrl: string | null;
  /** 科目・キャラクター・アイテム・ガチャの下書きの読み取り(Firestore cmsDrafts)。 */
  readonly catalogReader: CatalogDraftReader;
  /** 下書きの保存(cmsSaveDrafts callable。Firestore へ直接書かない)。 */
  readonly catalogSaver: CatalogDraftSaver;
  /** 公開版ルート(VITE_CONTENT_BASE)。合成プレビューの素材を読む。null ならプレビューを出さない。 */
  readonly contentBase: string | null;
  readonly projectId: string;
  readonly usingEmulator: boolean;
}

export type Bootstrap =
  ReadyBootstrap | { readonly kind: 'unconfigured'; readonly missing: string[] };

let cached: Bootstrap | null = null;

export function bootstrap(): Bootstrap {
  if (cached) return cached;
  const env = readAdminEnv();
  if (!env) {
    cached = { kind: 'unconfigured', missing: missingAdminEnvKeys() };
    return cached;
  }
  const firebase = createAdminFirebase(env);
  const assetsBase = readAssetsBase();
  const publicContentBase = readContentBase();
  const contentSource = resolveAdminContentSource({
    contentBase: publicContentBase,
    bundledBase: BUNDLED_CONTENT_BASE,
  });
  const contentBase = contentSource.then((source) => source.baseUrl);
  cached = {
    kind: 'ready',
    firebase,
    auth: new AdminAuthService(firebase),
    content: new ContentLoader({ baseUrl: contentBase }),
    quiz: new QuizLoader({ baseUrl: contentBase }),
    assetsBase,
    contentSource,
    cms: createCmsCallables(firebase.functions),
    cmsReader: createCmsReader(firebase.db),
    webAppUrl: readWebAppUrl(),
    catalogReader: createCatalogDraftReader(firebase.db),
    catalogSaver: createCatalogDraftSaver(firebase.functions),
    contentBase: publicContentBase,
    projectId: env.projectId,
    usingEmulator: env.emulator !== null,
  };
  return cached;
}
